import { type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { FolderOpen, Check, Sparkles } from "lucide-react";
import { cn } from "@/modern/utils";
import { MarketingTag } from "@/bryntum-lib/models/MarketingTag";

interface MarketingFolderSelectorProps {
  folderTags: MarketingTag[];
  selectedTagIds: number[];
  onToggle: (tagId: number) => void;
  getImageCount?: (tagId: number) => number;
  isAiSuggested?: (tagId: number) => boolean;
  renderExpandedContent?: (tag: MarketingTag) => ReactNode;
  /** Renders on the same row as the section title (e.g. step help). */
  headerEnd?: ReactNode;
}

export const MarketingFolderSelector = ({
  folderTags,
  selectedTagIds,
  onToggle,
  getImageCount,
  isAiSuggested,
  renderExpandedContent,
  headerEnd,
}: MarketingFolderSelectorProps) => {
  if (folderTags.length === 0 && headerEnd == null) return null;

  const headerBlock = (
    <>
      <div className="flex items-center justify-between gap-2 mb-1 min-h-7">
        <label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground leading-none">
          Content Role
        </label>
        {headerEnd != null ? <div className="flex shrink-0 items-center">{headerEnd}</div> : null}
      </div>
      <p className="text-sm text-muted-foreground/70 mb-4">What role does this image play?</p>
    </>
  );

  if (folderTags.length === 0) {
    return <div>{headerBlock}</div>;
  }

  return (
    <div>
      {headerBlock}
      <div className="space-y-1.5">
        {folderTags.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id);
          const imageCount = getImageCount?.(tag.id);
          const aiSuggested = isAiSuggested?.(tag.id) ?? false;
          const expandedContent = isSelected && renderExpandedContent ? renderExpandedContent(tag) : null;

          return (
            <div
              key={tag.id}
              className={cn(
                "rounded-lg border transition-colors duration-150 overflow-hidden",
                isSelected
                  ? "border-primary bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]"
                  : "border-border hover:border-muted-foreground/40",
              )}
            >
              <button
                type="button"
                onClick={() => onToggle(tag.id)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors duration-150",
                  isSelected ? "bg-primary/10" : "bg-transparent hover:bg-muted/30",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5">
                    <FolderOpen
                      className={cn(
                        "w-4 h-4 shrink-0 transition-colors duration-150",
                        isSelected ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <span
                      className={cn(
                        "text-sm font-medium truncate transition-colors",
                        isSelected ? "text-primary" : "text-foreground",
                      )}
                    >
                      {tag.label}
                    </span>
                    {imageCount != null && (
                      <span
                        className={cn(
                          "inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-bold tabular-nums leading-none transition-colors",
                          imageCount > 0
                            ? "bg-primary/20 text-primary ring-1 ring-inset ring-primary/35"
                            : "bg-muted text-muted-foreground ring-1 ring-inset ring-border/70",
                        )}
                      >
                        {imageCount}
                      </span>
                    )}
                    {aiSuggested && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-500/15 text-violet-400 shrink-0">
                        <Sparkles className="w-2.5 h-2.5" />
                        AI
                      </span>
                    )}
                  </div>
                  {tag.description ? (
                    <span className="text-xs text-muted-foreground/70 truncate block pl-[1.625rem] mt-0.5">
                      {tag.description}
                    </span>
                  ) : null}
                </div>

                <div
                  className={cn(
                    "w-4 h-4 rounded flex items-center justify-center shrink-0 transition-all duration-150",
                    isSelected
                      ? "bg-primary"
                      : "border border-muted-foreground/25 bg-transparent",
                  )}
                >
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 600, damping: 25 }}
                    >
                      <Check className="w-3 h-3 text-primary-foreground" />
                    </motion.div>
                  )}
                </div>
              </button>

              <AnimatePresence initial={false}>
                {expandedContent && (
                  <motion.div
                    key="expanded"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden border-t border-border/60"
                  >
                    {expandedContent}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
